// IMPORTANT: Pour que les fonctionnalités d'exportation Excel et PDF fonctionnent,
// vous DEVEZ inclure les liens CDN suivants dans la section <head> de votre
// fichier public/index.html (ou l'équivalent dans votre configuration de projet React) :
//
// <script src="https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.17.0/xlsx.full.min.js"></script>
// <script src="https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.3.1/jspdf.umd.min.js"></script>
// <script src="https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.5.14/jspdf.plugin.autotable.min.js"></script>
//
// Sans ces scripts, les fonctions d'exportation ne seront pas disponibles et provoqueront des erreurs.

import React, { useState, useEffect, useCallback } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import {
    AppBar, Toolbar, IconButton, Menu, MenuItem,
    Button, TextField, Box, Typography, Container, Paper,
    FormControl, InputLabel, Select, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, LinearProgress,
    Alert, CircularProgress, BottomNavigation, BottomNavigationAction
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu'; // Still needed for mobile if any other menu is added
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SettingsIcon from '@mui/icons-material/Settings';
import RefreshIcon from '@mui/icons-material/Refresh';
import PeopleIcon from '@mui/icons-material/People'; // Icon for 'Personnes'
import AssessmentIcon from '@mui/icons-material/Assessment'; // Icon for 'Résumé'
import DownloadIcon from '@mui/icons-material/Download'; // Icon for download
import * as XLSX from 'xlsx';


// Base URL for your PHP API
// IMPORTANT: Replace this with the actual URL where your PHP files are hosted
const API_BASE_URL = 'https://can-gestion.nick-web.be/api';

// Define a basic Material UI theme
const theme = createTheme({
    palette: {
        primary: {
            main: '#4f46e5', // Indigo 600
        },
        secondary: {
            main: '#10b981', // Green 500
        },
        error: {
            main: '#ef4444', // Red 500
        },
        warning: {
            main: '#f59e0b', // Yellow 500
        },
        info: {
            main: '#3b82f6', // Blue 500
        },
    },
    typography: {
        fontFamily: 'Inter, sans-serif',
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                    textTransform: 'none',
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: '12px',
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                },
            },
        },
    },
});

// Helper function to format phone number
const formatPhoneNumber = (value) => {
    if (!value) return '';
    const digits = value.replace(/\D/g, ''); // Remove non-digits
    let formatted = '';
    if (digits.length > 0) {
        formatted += digits.substring(0, 4);
    }
    if (digits.length > 4) {
        formatted += ' / ' + digits.substring(4, 6);
    }
    if (digits.length > 6) {
        formatted += ' ' + digits.substring(6, 8);
    }
    if (digits.length > 8) {
        formatted += ' ' + digits.substring(8, 10);
    }
    return formatted;
};

// Helper function to parse phone number (remove formatting)
const parsePhoneNumber = (value) => {
    return value.replace(/\D/g, '');
};


// Login Page Component
const LoginPage = ({ onLogin, message }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        onLogin(username, password);
    };

    return (
        <Container component="main" maxWidth="xs" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', bgcolor: 'grey.50' }}>
            <Paper elevation={6} sx={{ p: 4, borderRadius: '12px', width: '100%' }}>
                <Typography component="h1" variant="h5" align="center" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                    Connexion
                </Typography>
                <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="username"
                        label="Nom d'utilisateur"
                        name="username"
                        autoComplete="username"
                        autoFocus
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        variant="outlined"
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="password"
                        label="Mot de passe"
                        type="password"
                        id="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        variant="outlined"
                        sx={{ mb: 3 }}
                    />
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ mt: 1, mb: 2, py: 1.5 }}
                    >
                        Se connecter
                    </Button>
                    {message && (
                        <Typography variant="body2" color="error" align="center" sx={{ mt: 2 }}>
                            {message}
                        </Typography>
                    )}
                </Box>
            </Paper>
        </Container>
    );
};


// Main App Component
const App = () => {
    // Authentication states
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [currentUsername, setCurrentUsername] = useState('');
    const [authMessage, setAuthMessage] = useState('');

    // State for navigation between different views
    const [currentView, setCurrentView] = useState('people'); // 'people', 'attendance', 'summary', 'payments', 'managePayers', 'manageThemes', 'calendar'
    const [selectedPerson, setSelectedPerson] = useState(null); // Stores the person object when managing attendance/payments

    // New states for dynamic payers and themes
    const [allPayers, setAllPayers] = useState([]);
    const [allThemes, setAllThemes] = useState([]);

    // Global data states (fetched once after login)
    const [allPeople, setAllPeople] = useState([]); // All registered people
    const [allAttendanceRecords, setAllAttendanceRecords] = useState([]); // All attendance records for all people
    const [allPaymentRecords, setAllPaymentRecords] = useState([]); // All payment records for all people
    const [loadingGlobalData, setLoadingGlobalData] = useState(false);


    // State for BottomNavigation selected tab (0: People, 1: Summary, 2: Calendar, 3: Config)
    const [bottomNavValue, setBottomNavValue] = useState(0);
    const [anchorElConfig, setAnchorElConfig] = useState(null); // State for Config menu dropdown

    const handleOpenConfigMenu = (event) => {
        setAnchorElConfig(event.currentTarget);
    };

    const handleCloseConfigMenu = () => {
        setAnchorElConfig(null);
    };

    const handleNavigate = (view) => {
        setCurrentView(view);
        // Ensure bottomNavValue is updated if navigation happens outside BottomNavigation
        switch (view) {
            case 'people': setBottomNavValue(0); break;
            case 'summary': setBottomNavValue(1); break;
            case 'calendar': setBottomNavValue(2); break;
            // For 'managePayers' and 'manageThemes', keep 'Config' selected
            case 'managePayers':
            case 'manageThemes':
                setBottomNavValue(3);
                break;
            default: setBottomNavValue(0);
        }
        handleCloseConfigMenu(); // Close config menu if open
    };

    // Helper function to calculate summary for a single person
    const calculatePersonSummary = useCallback((personId, allAttendance, allPayments) => {
        let totalDays = 0;
        allAttendance.filter(record => parseInt(record.personId) === parseInt(personId)).forEach(record => {
            totalDays += parseInt(record.actualDaysPresent) || 0;
        });

        let totalDeductions = 0;
        allPayments.filter(payment => parseInt(payment.personId) === parseInt(personId)).forEach(data => {
            totalDeductions += parseFloat(data.amount); // amount is stored as negative for deductions
        });

        const totalEarnings = totalDays * 80; // Assuming 80€ per day
        const netAmount = totalEarnings + totalDeductions; // Deductions are negative, so addition effectively subtracts

        return { totalDays, totalEarnings, totalDeductions: Math.abs(totalDeductions), netAmount };
    }, []);


    // Function to fetch all people, payers, themes, attendance, and payments
    const fetchGlobalData = useCallback(async () => {
        setLoadingGlobalData(true);
        try {
            console.log("Fetching global data...");

            const peopleUrl = `${API_BASE_URL}/people.php`;
            const payersUrl = `${API_BASE_URL}/payers.php`;
            const themesUrl = `${API_BASE_URL}/themes.php`;
            const attendanceUrl = `${API_BASE_URL}/all_attendance.php`;
            const paymentsUrl = `${API_BASE_URL}/all_payments.php`;

            console.log("Fetching people from:", peopleUrl);
            console.log("Fetching payers from:", payersUrl);
            console.log("Fetching themes from:", themesUrl);
            console.log("Fetching all attendance from:", attendanceUrl);
            console.log("Fetching all payments from:", paymentsUrl);

            const [peopleRes, payersRes, themesRes, attendanceRes, paymentsRes] = await Promise.all([
                fetch(peopleUrl),
                fetch(payersUrl),
                fetch(themesUrl),
                fetch(attendanceUrl),
                fetch(paymentsUrl),
            ]);

            console.log("People response status:", peopleRes.status);
            console.log("Payers response status:", payersRes.status);
            console.log("Themes response status:", themesRes.status);
            console.log("All Attendance response status:", attendanceRes.status);
            console.log("All Payments response status:", paymentsRes.status);

            if (!peopleRes.ok) throw new Error(`HTTP error fetching people! status: ${peopleRes.status}`);
            if (!payersRes.ok) throw new Error(`HTTP error fetching payers! status: ${payersRes.status}`);
            if (!themesRes.ok) throw new Error(`HTTP error fetching themes! status: ${themesRes.status}`);
            if (!attendanceRes.ok) throw new Error(`HTTP error fetching all attendance! status: ${attendanceRes.status}`);
            if (!paymentsRes.ok) throw new Error(`HTTP error fetching all payments! status: ${paymentsRes.status}`);

            const peopleData = await peopleRes.json();
            const payersData = await payersRes.json();
            const themesData = await themesRes.json();
            const attendanceData = await attendanceRes.json();
            const paymentsData = await paymentsRes.json();

            setAllPeople(peopleData);
            setAllPayers(payersData);
            setAllThemes(themesData);
            setAllAttendanceRecords(attendanceData);
            setAllPaymentRecords(paymentsData);

            console.log("Global data fetched successfully.");

        } catch (error) {
            console.error("Erreur lors de la récupération des données globales:", error);
            // Show a more prominent error message to the user
            setAuthMessage(`Erreur lors du chargement des données: ${error.message}. Veuillez vérifier la connexion à l'API et les fichiers PHP.`);
        } finally {
            setLoadingGlobalData(false);
        }
    }, []);

    // Function to handle user login
    const handleLogin = async (username, password) => {
        setAuthMessage(''); // Clear previous messages
        try {
            const url = `${API_BASE_URL}/auth.php`;
            console.log("Attempting to log in to:", url); // Debugging log
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password })
            });

            console.log("Login response status:", response.status); // Debugging log

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const result = await response.json();

            if (result.success) {
                setIsLoggedIn(true);
                setCurrentUserId(result.userId);
                setCurrentUsername(result.username);
                setAuthMessage(result.message);
                setCurrentView('people'); // Redirect to people list after successful login
                fetchGlobalData(); // Fetch global data after successful login
            } else {
                setAuthMessage(result.message);
            }
        } catch (error) {
            console.error("Erreur de connexion:", error);
            setAuthMessage("Erreur réseau ou du serveur. Veuillez réessayer.");
        }
    };

    // Function to handle user logout
    const handleLogout = () => {
        setIsLoggedIn(false);
        setCurrentUserId(null);
        setCurrentUsername('');
        setAuthMessage("Vous avez été déconnecté.");
        setCurrentView('people'); // Go back to people list (or login page) after logout
        // Clear global data on logout
        setAllPeople([]);
        setAllAttendanceRecords([]);
        setAllPaymentRecords([]);
        setAllPayers([]);
        setAllThemes([]);
    };

    // --- Export Functions ---

    // Generic function to export data to Excel
    const exportToExcel = (data, sheetName, fileName) => {
        if (typeof XLSX === 'undefined') {
            console.error("XLSX library not loaded. Please ensure the CDN link is included in index.html.");
            alert("Erreur: La bibliothèque d'exportation Excel n'est pas chargée. Veuillez recharger la page ou contacter le support.");
            return;
        }
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
        XLSX.writeFile(wb, `${fileName}.xlsx`);
    };

    // Generic function to export data to PDF
    const exportToPdf = (data, columns, title, fileName) => {
        if (typeof jspdf === 'undefined' || typeof window.jspdf.jsPDF === 'undefined') {
            console.error("jsPDF library not loaded. Please ensure the CDN link is included in index.html.");
            alert("Erreur: La bibliothèque d'exportation PDF n'est pas chargée. Veuillez recharger la page ou contacter le support.");
            return;
        }
        const doc = new window.jspdf.jsPDF(); // Use window.jspdf.jsPDF
        doc.text(title, 14, 16); // Title
        doc.autoTable({
            startY: 20,
            head: [columns.map(col => col.header)],
            body: data.map(row => columns.map(col => row[col.dataKey])),
            theme: 'striped',
            styles: {
                font: 'helvetica',
                fontSize: 10,
                cellPadding: 3,
                valign: 'middle',
            },
            headStyles: {
                fillColor: theme.palette.primary.main,
                textColor: 255,
                fontStyle: 'bold',
            },
            alternateRowStyles: {
                fillColor: theme.palette.grey[100],
            },
        });
        doc.save(`${fileName}.pdf`);
    };


    // Component for managing people (add, list, edit)
    const PeopleList = ({ setCurrentView, setSelectedPerson, allPeople, allAttendanceRecords, allPaymentRecords, calculatePersonSummary, refreshGlobalData }) => {
        const [firstName, setFirstName] = useState('');
        const [phoneNumber, setPhoneNumber] = useState(''); // Stores formatted number
        const [message, setMessage] = useState(''); // For user feedback
        const [editingPerson, setEditingPerson] = useState(null); // State to hold the person being edited
        const [filterText, setFilterText] = useState(''); // New state for filter text

        // Function to fetch people data (only for the form, list will use allPeople prop)
        // This is kept for consistency but `refreshGlobalData` is the primary update trigger.
        const fetchPeople = useCallback(async () => {
            try {
                const url = `${API_BASE_URL}/people.php`;
                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                // This data is not directly used to update the list, as `allPeople` prop is the source.
                // It's here to ensure the API call is structured if needed for other purposes.
                // The `refreshGlobalData()` call below is what updates the list.
            } catch (error) {
                console.error("Erreur de récupération des personnes (interne):", error);
                setMessage("Erreur lors du chargement des personnes.");
            }
        }, []);

        // Handle adding a new person or updating an existing one
        const handleSavePerson = async (e) => {
            e.preventDefault();
            const parsedPhoneNumber = parsePhoneNumber(phoneNumber); // Parse before sending to API

            if (!firstName || !parsedPhoneNumber) {
                setMessage("Veuillez remplir tous les champs.");
                return;
            }

            try {
                let response;
                if (editingPerson) {
                    // Update existing person
                    response = await fetch(`${API_BASE_URL}/people.php`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ id: editingPerson.id, firstName, phoneNumber: parsedPhoneNumber })
                    });
                } else {
                    // Add a new person
                    response = await fetch(`${API_BASE_URL}/people.php`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ firstName, phoneNumber: parsedPhoneNumber })
                    });
                }

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const result = await response.json();
                setMessage(result.message);
                
                refreshGlobalData(); // Re-fetch all global data to update the list

                setEditingPerson(null); // Exit edit mode
                setFirstName('');
                setPhoneNumber('');
            } catch (error) {
                console.error("Erreur de sauvegarde de personne:", error);
                setMessage("Échec de la sauvegarde de la personne.");
            }
        };

        // Handle editing a person
        const handleEditPerson = (person) => {
            setEditingPerson(person);
            setFirstName(person.firstName);
            setPhoneNumber(formatPhoneNumber(person.phoneNumber)); // Format for display
            setMessage(''); // Clear any previous messages
        };

        // Handle canceling edit mode
        const handleCancelEdit = () => {
            setEditingPerson(null);
            setFirstName('');
            setPhoneNumber('');
            setMessage('');
        };

        // Navigate to attendance view for a specific person
        const handleViewAttendance = (person) => {
            setSelectedPerson(person);
            setCurrentView('attendance');
        };

        // Navigate to payments view for a specific person
        const handleViewPayments = (person) => {
            setSelectedPerson(person);
            setCurrentView('payments');
        };

        // Filtered list of people based on filterText
        const filteredPeople = allPeople.filter(person =>
            person.firstName.toLowerCase().includes(filterText.toLowerCase()) ||
            person.phoneNumber.toLowerCase().includes(parsePhoneNumber(filterText).toLowerCase()) // Search by parsed number
        );

        return (
            <Paper elevation={3} sx={{ p: 4, borderRadius: '12px', maxWidth: '960px', mx: 'auto', my: 4 }}>
                <Typography variant="h4" component="h2" align="center" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary', mb: 3 }}>
                    Gérer les Personnes
                </Typography>

                {/* Form to add/edit a person */}
                <Box component="form" onSubmit={handleSavePerson} sx={{ mb: 4, p: 3, border: '1px solid #e0e0e0', borderRadius: '8px', bgcolor: 'grey.50', boxShadow: 1 }}>
                    <Typography variant="h6" component="h3" sx={{ mb: 2, color: 'text.secondary' }}>
                        {editingPerson ? 'Modifier la Personne' : 'Ajouter une Nouvelle Personne'}
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                        <TextField
                            margin="normal"
                            fullWidth
                            label="Prénom"
                            variant="outlined"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            placeholder="Entrez le prénom"
                            required
                        />
                        <TextField
                            margin="normal"
                            fullWidth
                            label="Numéro de Téléphone"
                            variant="outlined"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))} // Format on change
                            placeholder="0000 / 00 00 00"
                            required
                            inputProps={{ maxLength: 16 }} // Max length for "0000 / 00 00 00"
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            sx={{ flexGrow: 1, py: 1.5 }}
                        >
                            {editingPerson ? 'Enregistrer les Modifications' : 'Ajouter la Personne'}
                        </Button>
                        {editingPerson && (
                            <Button
                                type="button"
                                variant="outlined"
                                color="inherit"
                                onClick={handleCancelEdit}
                                sx={{ flexGrow: 1, py: 1.5 }}
                            >
                                Annuler
                            </Button>
                        )}
                    </Box>
                    {message && <Typography variant="body2" align="center" sx={{ mt: 2, color: 'text.secondary' }}>{message}</Typography>}
                </Box>

                {/* Filter input */}
                <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: '8px', bgcolor: 'grey.50', boxShadow: 1 }}>
                    <TextField
                        fullWidth
                        label="Filtrer les Personnes"
                        variant="outlined"
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                        placeholder="Rechercher par prénom ou numéro de téléphone"
                    />
                </Box>

                {/* List of existing people */}
                <Typography variant="h6" component="h3" sx={{ mb: 2, color: 'text.secondary' }}>Liste des Personnes</Typography>
                {filteredPeople.length === 0 ? (
                    <Typography align="center" color="text.secondary">Aucune personne trouvée avec ce filtre.</Typography>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {filteredPeople.map((person) => {
                            const { netAmount } = calculatePersonSummary(person.id, allAttendanceRecords, allPaymentRecords);
                            return (
                                <Paper key={person.id} elevation={1} sx={{ p: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', borderRadius: '8px' }}>
                                    <Box sx={{ mb: { xs: 1, sm: 0 } }}>
                                        <Typography variant="h6" component="p" sx={{ fontWeight: 'medium' }}>{person.firstName}</Typography>
                                        <Typography variant="body2" color="text.secondary">{formatPhoneNumber(person.phoneNumber)}</Typography> {/* Display formatted number */}
                                        <Typography variant="body2" color="text.primary" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                            <AccountBalanceWalletIcon fontSize="small" sx={{ mr: 0.5 }} />
                                            Montant Net: <Box component="span" sx={{ fontWeight: 'bold', ml: 0.5, color: netAmount >= 0 ? 'success.main' : 'error.main' }}>
                                                {netAmount.toFixed(2)}€
                                            </Box>
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        <Button
                                            variant="contained"
                                            color="info"
                                            size="small"
                                            onClick={() => handleViewAttendance(person)}
                                        >
                                            Voir Présences
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="success"
                                            size="small"
                                            onClick={() => handleViewPayments(person)}
                                        >
                                            Gérer Paiements
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="warning"
                                            size="small"
                                            onClick={() => handleEditPerson(person)}
                                        >
                                            Modifier
                                        </Button>
                                    </Box>
                                </Paper>
                            );
                        })}
                    </Box>
                )}
            </Paper>
        );
    };

    // Component for tracking attendance
    const AttendanceTracker = ({ selectedPerson, setCurrentView, allThemes, refreshGlobalData, exportToExcel, exportToPdf }) => {
        const [startDate, setStartDate] = useState('');
        const [endDate, setEndDate] = useState('');
        const [actualDaysPresent, setActualDaysPresent] = useState('');
        const [theme, setTheme] = useState('');
        const [attendanceRecords, setAttendanceRecords] = useState([]); // Local state for specific person's attendance
        const [message, setMessage] = useState('');
        const [editingAttendance, setEditingAttendance] = useState(null);

        // Calculate number of days between two dates (inclusive)
        const calculateDaysBetweenDates = (start, end) => {
            const oneDay = 24 * 60 * 60 * 1000;
            const diffTime = Math.abs(new Date(end).getTime() - new Date(start).getTime());
            const diffDays = Math.ceil(diffTime / oneDay) + 1;
            return diffDays;
        };

        // Effect to calculate default actualDaysPresent when startDate or endDate changes
        useEffect(() => {
            if (startDate && endDate && !editingAttendance) {
                const start = new Date(startDate);
                const end = new Date(endDate);
                if (start <= end) {
                    setActualDaysPresent(calculateDaysBetweenDates(start, end).toString());
                } else {
                    setActualDaysPresent('');
                }
            }
        }, [startDate, endDate, editingAttendance]);

        // Function to fetch attendance records for the selected person
        const fetchAttendanceForPerson = useCallback(async () => {
            if (!selectedPerson) return;
            try {
                const url = `${API_BASE_URL}/attendance.php?personId=${selectedPerson.id}`;
                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                const records = data.map(record => ({
                    ...record,
                    startDate: new Date(record.startDate),
                    endDate: new Date(record.endDate)
                }));
                setAttendanceRecords(records);
            } catch (error) {
                console.error("Erreur de récupération des présences:", error);
                setMessage("Erreur lors du chargement des présences.");
            }
        }, [selectedPerson]);

        // Fetch attendance records on component mount or selectedPerson change
        useEffect(() => {
            fetchAttendanceForPerson();
        }, [fetchAttendanceForPerson]);

        // Handle adding a new attendance record or updating an existing one
        const handleSaveAttendance = async (e) => {
            e.preventDefault();
            if (!selectedPerson) {
                setMessage("Aucune personne n'est sélectionnée.");
                return;
            }
            if (!startDate || !endDate || actualDaysPresent === '') {
                setMessage("Veuillez sélectionner les dates de début et de fin et le nombre de jours de présence.");
                return;
            }
            if (!theme) {
                setMessage("Veuillez sélectionner un thème.");
                return;
            }

            const start = new Date(startDate);
            const end = new Date(endDate);
            const days = parseInt(actualDaysPresent, 10);

            if (start > end) {
                setMessage("La date de début ne peut pas être postérieure à la date de fin.");
                return;
            }
            if (isNaN(days) || days < 0) {
                setMessage("Veuillez entrer un nombre de jours de présence valide et positif.");
                return;
            }
            const totalDaysInRange = calculateDaysBetweenDates(start, end);
            if (days > totalDaysInRange) {
                setMessage(`Le nombre de jours de présence ne peut pas dépasser ${totalDaysInRange} pour cette période.`);
                return;
            }

            try {
                let response;
                const recordData = {
                    personId: selectedPerson.id,
                    startDate: startDate, // Send as string for PHP
                    endDate: endDate,     // Send as string for PHP
                    actualDaysPresent: days,
                    theme: theme,
                };

                if (editingAttendance) {
                    // Update existing attendance record
                    response = await fetch(`${API_BASE_URL}/attendance.php`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ id: editingAttendance.id, ...recordData })
                    });
                } else {
                    // Add a new attendance record
                    response = await fetch(`${API_BASE_URL}/attendance.php`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(recordData)
                    });
                }

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const result = await response.json();
                setMessage(result.message);
                
                fetchAttendanceForPerson(); // Re-fetch local data
                refreshGlobalData(); // Trigger global data refresh for summary/calendar views

                setEditingAttendance(null); // Exit edit mode
                setStartDate('');
                setEndDate('');
                setActualDaysPresent('');
                setTheme('');
            } catch (error) {
                console.error("Erreur d'enregistrement/modification de présence:", error);
                setMessage("Échec de l'enregistrement/modification de la présence.");
            }
        };

        // Handle editing an attendance record
        const handleEditAttendance = (record) => {
            setEditingAttendance(record);
            setStartDate(record.startDate.toISOString().split('T')[0]); // Format date for input
            setEndDate(record.endDate.toISOString().split('T')[0]);     // Format date for input
            setActualDaysPresent(record.actualDaysPresent.toString());
            setTheme(record.theme || ''); // Set theme, default to empty if not present
            setMessage('');
        };

        // Handle canceling edit mode
        const handleCancelEdit = () => {
            setEditingAttendance(null);
            setStartDate('');
            setEndDate('');
            setActualDaysPresent('');
            setTheme('');
            setMessage('');
        };

        // Export attendance data
        const handleExportAttendance = (type) => {
            const dataToExport = attendanceRecords.map(record => ({
                'ID Personne': record.personId,
                'Prénom': selectedPerson.firstName,
                'Date de Début': new Date(record.startDate).toLocaleDateString(),
                'Date de Fin': new Date(record.endDate).toLocaleDateString(),
                'Jours Réels Présents': record.actualDaysPresent,
                'Thème': record.theme,
            }));

            const columns = [
                { header: 'ID Personne', dataKey: 'ID Personne' },
                { header: 'Prénom', dataKey: 'Prénom' },
                { header: 'Date de Début', dataKey: 'Date de Début' },
                { header: 'Date de Fin', dataKey: 'Date de Fin' },
                { header: 'Jours Réels Présents', dataKey: 'Jours Réels Présents' },
                { header: 'Thème', dataKey: 'Thème' },
            ];

            if (type === 'excel') {
                exportToExcel(dataToExport, `Présences ${selectedPerson.firstName}`, `presences_${selectedPerson.firstName}`);
            } else if (type === 'pdf') {
                exportToPdf(dataToExport, columns, `Historique des Présences pour ${selectedPerson.firstName}`, `presences_${selectedPerson.firstName}`);
            }
        };


        return (
            <Paper elevation={3} sx={{ p: 4, borderRadius: '12px', maxWidth: '960px', mx: 'auto', my: 4 }}>
                <Typography variant="h4" component="h2" align="center" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary', mb: 3 }}>
                    Gérer les Présences pour {selectedPerson?.firstName}
                </Typography>

                {/* Back button and Export buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setCurrentView('people')}
                    >
                        Retour à la Liste des Personnes
                    </Button>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<DownloadIcon />}
                            onClick={() => handleExportAttendance('excel')}
                        >
                            Exporter Excel
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<DownloadIcon />}
                            onClick={() => handleExportAttendance('pdf')}
                        >
                            Exporter PDF
                        </Button>
                    </Box>
                </Box>

                {/* Form to add/edit attendance */}
                <Box component="form" onSubmit={handleSaveAttendance} sx={{ mb: 4, p: 3, border: '1px solid #e0e0e0', borderRadius: '8px', bgcolor: 'grey.50', boxShadow: 1 }}>
                    <Typography variant="h6" component="h3" sx={{ mb: 2, color: 'text.secondary' }}>
                        {editingAttendance ? 'Modifier la Présence' : 'Ajouter une Période de Présence'}
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 2 }}>
                        <TextField
                            fullWidth
                            label="Date de Début"
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            required
                        />
                        <TextField
                            fullWidth
                            label="Date de Fin"
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            required
                        />
                        <TextField
                            fullWidth
                            label="Jours de Présence Réels"
                            type="number"
                            value={actualDaysPresent}
                            onChange={(e) => setActualDaysPresent(e.target.value)}
                            placeholder="Nombre de jours"
                            inputProps={{ min: 0 }}
                            required
                        />
                    </Box>
                    <FormControl fullWidth sx={{ mb: 3 }}>
                        <InputLabel id="theme-select-label">Thème</InputLabel>
                        <Select
                            labelId="theme-select-label"
                            id="theme"
                            value={theme}
                            label="Thème"
                            onChange={(e) => setTheme(e.target.value)}
                            required
                        >
                            <MenuItem value="">Sélectionner un thème</MenuItem>
                            {allThemes.map((t) => (
                                <MenuItem key={t.id} value={t.name}>{t.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            sx={{ flexGrow: 1, py: 1.5 }}
                        >
                            {editingAttendance ? 'Enregistrer les Modifications' : 'Enregistrer la Présence'}
                        </Button>
                        {editingAttendance && (
                            <Button
                                type="button"
                                variant="outlined"
                                color="inherit"
                                onClick={handleCancelEdit}
                                sx={{ flexGrow: 1, py: 1.5 }}
                            >
                                Annuler
                            </Button>
                        )}
                    </Box>
                    {message && <Typography variant="body2" align="center" sx={{ mt: 2, color: 'text.secondary' }}>{message}</Typography>}
                </Box>

                {/* List of attendance records */}
                <Typography variant="h6" component="h3" sx={{ mb: 2, color: 'text.secondary' }}>Historique des Présences</Typography>
                {attendanceRecords.length === 0 ? (
                    <Typography align="center" color="text.secondary">Aucune présence enregistrée pour cette personne.</Typography>
                ) : (
                    <TableContainer component={Paper} elevation={1} sx={{ borderRadius: '8px' }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Période</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Jours Réels</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Thème</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {attendanceRecords.map((record) => (
                                    <TableRow key={record.id}>
                                        <TableCell>
                                            <Typography variant="body2">Du: {new Date(record.startDate).toLocaleDateString()} au {new Date(record.endDate).toLocaleDateString()}</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2">{record.actualDaysPresent}</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2">{record.theme || 'Non spécifié'}</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button
                                                variant="contained"
                                                color="warning"
                                                size="small"
                                                onClick={() => handleEditAttendance(record)}
                                            >
                                                Modifier
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        );
    };

    // Component for a simple bar chart using HTML/CSS
    const SimpleBarChart = ({ data, title, barColor = 'primary.main' }) => {
        if (!data || data.length === 0 || data.every(d => d.value === 0)) {
            return (
                <Typography align="center" color="text.secondary" sx={{ mt: 2 }}>
                    Aucune donnée pour le graphique.
                </Typography>
            );
        }

        const maxValue = Math.max(...data.map(d => Math.abs(d.value))); // Use abs for max value to handle negative deductions

        return (
            <Paper elevation={1} sx={{ p: 2, borderRadius: '8px', bgcolor: 'grey.50', boxShadow: 1 }}>
                <Typography variant="h6" component="h4" align="center" sx={{ mb: 2, color: 'text.secondary' }}>{title}</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {data.map((item, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ width: '96px', textAlign: 'right', pr: 1, color: 'text.secondary', fontWeight: 'medium', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {item.category}:
                            </Typography>
                            <Box sx={{ flexGrow: 1, bgcolor: 'grey.200', borderRadius: '9999px', height: '24px', position: 'relative', overflow: 'hidden' }}>
                                <Box
                                    sx={{
                                        bgcolor: item.value < 0 ? 'error.main' : barColor, // Deductions in red
                                        height: '100%',
                                        borderRadius: '9999px',
                                        transition: 'width 0.5s ease-out',
                                        width: `${(Math.abs(item.value) / maxValue) * 100}%`
                                    }}
                                ></Box>
                                <Typography variant="caption" sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', color: 'text.primary' }}>
                                    {item.value.toFixed(2)}€
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Paper>
        );
    };

    // Component for viewing summary (total days, earnings, net, payment summary)
    const SummaryView = ({ setCurrentView, allPeople, allAttendanceRecords, allPaymentRecords, calculatePersonSummary, exportToExcel, exportToPdf }) => {
        const [summaryData, setSummaryData] = useState([]);
        const [paymentSummaryByPayerAndType, setPaymentSummaryByPayerAndType] = useState({});
        const [totalPaymentsByPayerChartData, setTotalPaymentsByPayerChartData] = useState([]);
        const [totalPaymentsByTypeChartData, setTotalPaymentsByTypeChartData] = useState([]);
        const [totalNetSum, setTotalNetSum] = useState(0); // New state for total net sum
        const [message, setMessage] = useState('');

        const calculateSummary = useCallback(() => {
            const detailedSummary = [];
            let totalOverallNet = 0;
            const tempPaymentSummaryByPayerAndType = {};
            const tempTotalPaymentsByPayer = {};
            const tempTotalPaymentsByType = {};

            if (!allPeople || !allAttendanceRecords || !allPaymentRecords) {
                setMessage("Données de résumé non disponibles.");
                return;
            }

            allPeople.forEach(person => {
                const { totalDays, totalEarnings, totalDeductions, netAmount } = calculatePersonSummary(person.id, allAttendanceRecords, allPaymentRecords);
                
                detailedSummary.push({
                    ...person,
                    totalDays,
                    totalEarnings,
                    totalDeductions,
                    netAmount
                });
                totalOverallNet += netAmount;
            });

            // Calculate payments summary
            allPaymentRecords.forEach(data => {
                const payer = data.payer || 'Inconnu';
                const type = data.paymentType || 'Non spécifié';
                const amount = Math.abs(parseFloat(data.amount)); // Deductions are stored as negative, use abs for display

                if (!tempPaymentSummaryByPayerAndType[payer]) {
                    tempPaymentSummaryByPayerAndType[payer] = {};
                }
                if (!tempPaymentSummaryByPayerAndType[payer][type]) {
                    tempPaymentSummaryByPayerAndType[payer][type] = 0;
                }
                tempPaymentSummaryByPayerAndType[payer][type] += amount;

                if (!tempTotalPaymentsByPayer[payer]) {
                    tempTotalPaymentsByPayer[payer] = 0;
                }
                tempTotalPaymentsByPayer[payer] += amount;

                if (!tempTotalPaymentsByType[type]) {
                    tempTotalPaymentsByType[type] = 0;
                }
                tempTotalPaymentsByType[type] += amount;
            });

            setSummaryData(detailedSummary);
            setTotalNetSum(totalOverallNet);
            setPaymentSummaryByPayerAndType(tempPaymentSummaryByPayerAndType);

            const chartDataByPayer = Object.entries(tempTotalPaymentsByPayer).map(([category, value]) => ({ category, value }));
            const chartDataByType = Object.entries(tempTotalPaymentsByType).map(([category, value]) => ({ category, value }));

            setTotalPaymentsByPayerChartData(chartDataByPayer);
            setTotalPaymentsByTypeChartData(chartDataByType);
            setMessage('');
        }, [allPeople, allAttendanceRecords, allPaymentRecords, calculatePersonSummary]);

        useEffect(() => {
            calculateSummary();
        }, [calculateSummary]);

        // Export summary data
        const handleExportSummary = (type) => {
            // Prepare data for per-person summary
            const personSummaryExportData = summaryData.map(person => ({
                'Prénom': person.firstName,
                'Jours Totaux': person.totalDays,
                'Gains Totaux (€)': person.totalEarnings.toFixed(2),
                'Déductions Totales (€)': person.totalDeductions.toFixed(2),
                'Montant Net (€)': person.netAmount.toFixed(2),
            }));

            // Prepare data for payment summary by payer and type
            const paymentSummaryExportData = [];
            Object.entries(paymentSummaryByPayerAndType).forEach(([payer, types]) => {
                Object.entries(types).forEach(([type, amount]) => {
                    paymentSummaryExportData.push({
                        'Payeur': payer,
                        'Type de Paiement': type,
                        'Montant Total (€)': amount.toFixed(2),
                    });
                });
            });

            if (type === 'excel') {
                // For Excel, we can create multiple sheets or combine data
                // For simplicity, let's export the main summary and payment summary separately for now
                exportToExcel(personSummaryExportData, 'Résumé par Personne', 'resume_global_personnes');
                if (paymentSummaryExportData.length > 0) {
                    exportToExcel(paymentSummaryExportData, 'Résumé Paiements', 'resume_global_paiements');
                }
            } else if (type === 'pdf') {
                const personSummaryColumns = [
                    { header: 'Prénom', dataKey: 'Prénom' },
                    { header: 'Jours Totaux', dataKey: 'Jours Totaux' },
                    { header: 'Gains Totaux (€)', dataKey: 'Gains Totaux (€)' },
                    { header: 'Déductions Totales (€)', dataKey: 'Déductions Totales (€)' },
                    { header: 'Montant Net (€)', dataKey: 'Montant Net (€)' },
                ];
                exportToPdf(personSummaryExportData, personSummaryColumns, 'Résumé Global par Personne', 'resume_global_personnes');

                if (paymentSummaryExportData.length > 0) {
                    const paymentSummaryColumns = [
                        { header: 'Payeur', dataKey: 'Payeur' },
                        { header: 'Type de Paiement', dataKey: 'Type de Paiement' },
                        { header: 'Montant Total (€)', dataKey: 'Montant Total (€)' },
                    ];
                    exportToPdf(paymentSummaryExportData, paymentSummaryColumns, 'Récapitulatif des Paiements par Payeur et Type', 'resume_global_paiements');
                }
            }
        };


        return (
            <Paper elevation={3} sx={{ p: 4, borderRadius: '12px', maxWidth: '960px', mx: 'auto', my: 4 }}>
                <Typography variant="h4" component="h2" align="center" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary', mb: 3 }}>
                    Résumé Global
                </Typography>

                {/* Total Net Sum Display */}
                <Paper elevation={2} sx={{ p: 2, mb: 4, bgcolor: 'primary.light', color: 'primary.contrastText', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <AccountBalanceWalletIcon sx={{ mr: 1, fontSize: '2rem' }} />
                    <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold' }}>
                        Total Net de l'Application : {totalNetSum.toFixed(2)}€
                    </Typography>
                </Paper>

                {/* Back button and Export buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setCurrentView('people')}
                    >
                        Retour à la Liste des Personnes
                    </Button>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<DownloadIcon />}
                            onClick={() => handleExportSummary('excel')}
                        >
                            Exporter Excel
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<DownloadIcon />}
                            onClick={() => handleExportSummary('pdf')}
                        >
                            Exporter PDF
                        </Button>
                    </Box>
                </Box>

                {message && <Alert severity="error" sx={{ mb: 3 }}>{message}</Alert>}

                <Typography variant="h6" component="h3" sx={{ mb: 2, color: 'text.secondary' }}>Résumé par Personne</Typography>
                {summaryData.length === 0 ? (
                    <Typography align="center" color="text.secondary">Aucune donnée de résumé disponible.</Typography>
                ) : (
                    <TableContainer component={Paper} elevation={1} sx={{ borderRadius: '8px', mb: 4 }}>
                        <Table sx={{ minWidth: 650 }} aria-label="summary table">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Prénom</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Jours Totaux</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Gains Totaux (€)</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Déductions Totales (€)</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Montant Net (€)</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {summaryData.map((person) => (
                                    <TableRow key={person.id}>
                                        <TableCell component="th" scope="row">
                                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{person.firstName}</Typography>
                                        </TableCell>
                                        <TableCell align="right"><Typography variant="body2">{person.totalDays}</Typography></TableCell>
                                        <TableCell align="right"><Typography variant="body2">{person.totalEarnings.toFixed(2)}</Typography></TableCell>
                                        <TableCell align="right"><Typography variant="body2">{person.totalDeductions.toFixed(2)}</Typography></TableCell>
                                        <TableCell align="right"><Typography variant="body1" sx={{ fontWeight: 'semibold' }}>{person.netAmount.toFixed(2)}</Typography></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <Typography variant="h6" component="h3" sx={{ mb: 2, mt: 4, color: 'text.secondary' }}>Récapitulatif des Paiements par Payeur et Type</Typography>
                {Object.keys(paymentSummaryByPayerAndType).length === 0 ? (
                    <Typography align="center" color="text.secondary">Aucun paiement enregistré pour le moment.</Typography>
                ) : (
                    <TableContainer component={Paper} elevation={1} sx={{ borderRadius: '8px', mb: 4 }}>
                        <Table sx={{ minWidth: 650 }} aria-label="payment summary by payer and type table">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Payeur</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Type de Paiement</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Montant Total (€)</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Object.entries(paymentSummaryByPayerAndType).map(([payer, types]) => (
                                    Object.entries(types).map(([type, amount]) => (
                                        <TableRow key={`${payer}-${type}`}>
                                            <TableCell><Typography variant="body2" sx={{ fontWeight: 'medium' }}>{payer}</Typography></TableCell>
                                            <TableCell><Typography variant="body2">{type}</Typography></TableCell>
                                            <TableCell align="right"><Typography variant="body2" sx={{ fontWeight: 'semibold' }}>{amount.toFixed(2)}</Typography></TableCell>
                                        </TableRow>
                                    ))
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <Typography variant="h6" component="h3" sx={{ mb: 2, mt: 4, color: 'text.secondary' }}>Statistiques des Paiements</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                    <SimpleBarChart
                        data={totalPaymentsByPayerChartData}
                        title="Paiements Totaux par Payeur"
                        barColor="primary.main"
                    />
                    <SimpleBarChart
                        data={totalPaymentsByTypeChartData}
                        title="Paiements Totaux par Type"
                        barColor="secondary.main"
                    />
                </Box>
            </Paper>
        );
    };

    // Component for adjusting payments
    const PaymentAdjustment = ({ selectedPerson, setCurrentView, allPayers, refreshGlobalData, exportToExcel, exportToPdf }) => {
        const [payments, setPayments] = useState([]);
        const [message, setMessage] = useState('');
        const [customAmount, setCustomAmount] = useState(''); // State for custom amount
        const [payerName, setPayerName] = useState(''); // State for payer's name
        const [paymentType, setPaymentType] = useState(''); // New state for payment type
        const [paymentDate, setPaymentDate] = useState(''); // New state for payment date
        const [editingPayment, setEditingPayment] = useState(null); // State for editing existing payment

        // List of possible payment types (still hardcoded for now, but can be dynamic)
        const paymentTypes = ['Benevolat', 'Facture', 'Autre'];

        // Function to fetch payment records for the selected person
        const fetchPaymentsForPerson = useCallback(async () => {
            if (!selectedPerson) return;
            try {
                const url = `${API_BASE_URL}/payments.php?personId=${selectedPerson.id}`;
                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                // Dates from PHP are strings, convert them to Date objects for consistency
                const records = data.map(record => ({
                    ...record,
                    date: new Date(record.date)
                }));
                setPayments(records);
            } catch (error) {
                console.error("Erreur de récupération des paiements:", error);
                setMessage("Erreur lors du chargement des paiements.");
            }
        }, [selectedPerson]);

        // Fetch payment records on component mount or selectedPerson change
        useEffect(() => {
            fetchPaymentsForPerson();
        }, [fetchPaymentsForPerson]);

        // Handle adding a payment deduction or updating an existing one
        const handleSavePayment = async () => {
            if (!selectedPerson) {
                setMessage("Aucune personne n'est sélectionnée.");
                return;
            }

            const amount = parseFloat(customAmount);

            if (isNaN(amount) || amount <= 0) {
                setMessage("Veuillez entrer un montant valide et positif.");
                return;
            }

            if (!payerName) {
                setMessage("Veuillez sélectionner qui a payé.");
                return;
            }

            if (!paymentType) {
                setMessage("Veuillez sélectionner le type de paiement.");
                return;
            }

            if (!paymentDate) {
                setMessage("Veuillez sélectionner la date du paiement.");
                return;
            }

            try {
                let response;
                const paymentData = {
                    personId: selectedPerson.id,
                    amount: -amount, // Store as negative for deductions
                    payer: payerName,
                    paymentType: paymentType,
                    date: paymentDate, // Include the date
                };

                if (editingPayment) {
                    // Update existing payment
                    response = await fetch(`${API_BASE_URL}/payments.php`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ id: editingPayment.id, ...paymentData })
                    });
                } else {
                    // Add new payment
                    response = await fetch(`${API_BASE_URL}/payments.php`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(paymentData)
                    });
                }

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const result = await response.json();
                setMessage(result.message);
                
                fetchPaymentsForPerson(); // Re-fetch local data
                refreshGlobalData(); // Trigger global data refresh for summary/calendar views

                setEditingPayment(null); // Exit edit mode
                setCustomAmount('');
                setPayerName('');
                setPaymentType('');
                setPaymentDate(''); // Clear date
            } catch (error) {
                console.error("Erreur d'enregistrement/modification de paiement:", error);
                setMessage("Échec de l'enregistrement/modification du paiement.");
            }
        };

        // Handle editing a payment
        const handleEditPayment = (payment) => {
            setEditingPayment(payment);
            setCustomAmount(Math.abs(parseFloat(payment.amount)).toString()); // Amount is stored negative, display positive for editing
            setPayerName(payment.payer || '');
            setPaymentType(payment.paymentType || '');
            setPaymentDate(new Date(payment.date).toISOString().split('T')[0]); // Format date for input
            setMessage('');
        };

        // Handle canceling edit mode
        const handleCancelEdit = () => {
            setEditingPayment(null);
            setCustomAmount('');
            setPayerName('');
            setPaymentType('');
            setPaymentDate('');
            setMessage('');
        };

        // Export payment data
        const handleExportPayments = (type) => {
            const dataToExport = payments.map(payment => ({
                'ID Personne': selectedPerson.id,
                'Prénom': selectedPerson.firstName,
                'Montant (€)': payment.amount,
                'Date': new Date(payment.date).toLocaleDateString(),
                'Payeur': payment.payer,
                'Type de Paiement': payment.paymentType,
            }));

            const columns = [
                { header: 'ID Personne', dataKey: 'ID Personne' },
                { header: 'Prénom', dataKey: 'Prénom' },
                { header: 'Montant (€)', dataKey: 'Montant (€)' },
                { header: 'Date', dataKey: 'Date' },
                { header: 'Payeur', dataKey: 'Payeur' },
                { header: 'Type de Paiement', dataKey: 'Type de Paiement' },
            ];

            if (type === 'excel') {
                exportToExcel(dataToExport, `Paiements ${selectedPerson.firstName}`, `paiements_${selectedPerson.firstName}`);
            } else if (type === 'pdf') {
                exportToPdf(dataToExport, columns, `Historique des Paiements pour ${selectedPerson.firstName}`, `paiements_${selectedPerson.firstName}`);
            }
        };

        return (
            <Paper elevation={3} sx={{ p: 4, borderRadius: '12px', maxWidth: '960px', mx: 'auto', my: 4 }}>
                <Typography variant="h4" component="h2" align="center" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary', mb: 3 }}>
                    Gérer les Paiements pour {selectedPerson?.firstName}
                </Typography>

                {/* Back button and Export buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setCurrentView('people')}
                    >
                        Retour à la Liste des Personnes
                    </Button>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<DownloadIcon />}
                            onClick={() => handleExportPayments('excel')}
                        >
                            Exporter Excel
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<DownloadIcon />}
                            onClick={() => handleExportPayments('pdf')}
                        >
                            Exporter PDF
                        </Button>
                    </Box>
                </Box>

                <Box sx={{ mb: 4, p: 3, border: '1px solid #e0e0e0', borderRadius: '8px', bgcolor: 'grey.50', boxShadow: 1, textAlign: 'center' }}>
                    <Typography variant="h6" component="h3" sx={{ mb: 2, color: 'text.secondary' }}>
                        {editingPayment ? 'Modifier la Déduction' : 'Effectuer une Nouvelle Déduction'}
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
                        <TextField
                            fullWidth
                            label="Montant à retirer (€)"
                            type="number"
                            value={customAmount}
                            onChange={(e) => setCustomAmount(e.target.value)}
                            placeholder="Montant"
                            inputProps={{ min: 0, step: 'any' }}
                            required
                        />
                        <FormControl fullWidth>
                            <InputLabel id="payer-select-label">Qui a payé ?</InputLabel>
                            <Select
                                labelId="payer-select-label"
                                id="payerName"
                                value={payerName}
                                label="Qui a payé ?"
                                onChange={(e) => setPayerName(e.target.value)}
                                required
                            >
                                <MenuItem value="">Sélectionner un payeur</MenuItem>
                                {allPayers.map((payer) => (
                                    <MenuItem key={payer.id} value={payer.name}>{payer.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel id="payment-type-select-label">Type de Paiement</InputLabel>
                            <Select
                                labelId="payment-type-select-label"
                                id="paymentType"
                                value={paymentType}
                                label="Type de Paiement"
                                onChange={(e) => setPaymentType(e.target.value)}
                                required
                            >
                                <MenuItem value="">Sélectionner un type</MenuItem>
                                {paymentTypes.map((type) => (
                                    <MenuItem key={type} value={type}>{type}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            label="Date du Paiement"
                            type="date"
                            value={paymentDate}
                            onChange={(e) => setPaymentDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            required
                            sx={{ gridColumn: { xs: 'span 1', md: 'span 3' } }} // Span full width on small screens, or 3 columns on medium
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="contained"
                            color="error"
                            fullWidth
                            onClick={handleSavePayment}
                            sx={{ py: 1.5, fontSize: '1.125rem', fontWeight: 'bold' }}
                        >
                            {editingPayment ? 'Enregistrer les Modifications' : 'Retirer le Montant'}
                        </Button>
                        {editingPayment && (
                            <Button
                                type="button"
                                variant="outlined"
                                color="inherit"
                                onClick={handleCancelEdit}
                                sx={{ flexGrow: 1, py: 1.5 }}
                            >
                                Annuler
                            </Button>
                        )}
                    </Box>
                    {message && <Typography variant="body2" align="center" sx={{ mt: 2, color: 'text.secondary' }}>{message}</Typography>}
                </Box>

                <Typography variant="h6" component="h3" sx={{ mb: 2, color: 'text.secondary' }}>Historique des Déductions</Typography>
                {payments.length === 0 ? (
                    <Typography align="center" color="text.secondary">Aucune déduction enregistrée pour cette personne.</Typography>
                ) : (
                    <TableContainer component={Paper} elevation={1} sx={{ borderRadius: '8px' }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Montant</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Payeur</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {payments.map((payment) => (
                                    <TableRow key={payment.id}>
                                        <TableCell><Typography variant="body2" sx={{ fontWeight: 'medium' }}>{payment.amount}€</Typography></TableCell>
                                        <TableCell><Typography variant="body2">{new Date(payment.date).toLocaleDateString()}</Typography></TableCell>
                                        <TableCell><Typography variant="body2" sx={{ fontWeight: 'semibold' }}>{payment.payer || 'Inconnu'}</Typography></TableCell>
                                        <TableCell><Typography variant="body2">{payment.paymentType || 'Non spécifié'}</Typography></TableCell>
                                        <TableCell align="right">
                                            <Button
                                                variant="contained"
                                                color="warning"
                                                size="small"
                                                onClick={() => handleEditPayment(payment)}
                                            >
                                                Modifier
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        );
    };

    // Component for managing payers
    const ManagePayers = ({ setCurrentView, fetchAllPayers, allPayers, refreshGlobalData }) => {
        const [payerName, setPayerName] = useState('');
        const [message, setMessage] = useState('');
        const [editingPayer, setEditingPayer] = useState(null);

        const handleSavePayer = async (e) => {
            e.preventDefault();
            if (!payerName) {
                setMessage("Veuillez entrer un nom de payeur.");
                return;
            }

            try {
                let response;
                if (editingPayer) {
                    response = await fetch(`${API_BASE_URL}/payers.php`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ id: editingPayer.id, name: payerName })
                    });
                } else {
                    response = await fetch(`${API_BASE_URL}/payers.php`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ name: payerName })
                    });
                }

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const result = await response.json();
                setMessage(result.message);
                setPayerName('');
                setEditingPayer(null);
                refreshGlobalData(); // Refresh the list in App.js
            } catch (error) {
                console.error("Erreur de sauvegarde du payeur:", error);
                setMessage("Échec de la sauvegarde du payeur.");
            }
        };

        const handleEditPayer = (payer) => {
            setEditingPayer(payer);
            setPayerName(payer.name);
            setMessage('');
        };

        const handleCancelEdit = () => {
            setEditingPayer(null);
            setPayerName('');
            setMessage('');
        };

        const handleDeletePayer = async (id) => {
            if (window.confirm("Êtes-vous sûr de vouloir supprimer ce payeur ?")) {
                try {
                    const response = await fetch(`${API_BASE_URL}/payers.php`, {
                        method: 'DELETE',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ id })
                    });
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    const result = await response.json();
                    setMessage(result.message);
                    refreshGlobalData(); // Refresh the list in App.js
                } catch (error) {
                    console.error("Erreur de suppression du payeur:", error);
                    setMessage("Échec de la suppression du payeur.");
                }
            }
        };

        return (
            <Paper elevation={3} sx={{ p: 4, borderRadius: '12px', maxWidth: '960px', mx: 'auto', my: 4 }}>
                <Typography variant="h4" component="h2" align="center" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary', mb: 3 }}>
                    Gérer les Payeurs
                </Typography>

                <Button
                    variant="outlined"
                    sx={{ mb: 3 }}
                    onClick={() => setCurrentView('people')}
                >
                    Retour à la Liste des Personnes
                </Button>

                <Box component="form" onSubmit={handleSavePayer} sx={{ mb: 4, p: 3, border: '1px solid #e0e0e0', borderRadius: '8px', bgcolor: 'grey.50', boxShadow: 1 }}>
                    <Typography variant="h6" component="h3" sx={{ mb: 2, color: 'text.secondary' }}>
                        {editingPayer ? 'Modifier le Payeur' : 'Ajouter un Nouveau Payeur'}
                    </Typography>
                    <TextField
                        fullWidth
                        label="Nom du Payeur"
                        variant="outlined"
                        value={payerName}
                        onChange={(e) => setPayerName(e.target.value)}
                        placeholder="Entrez le nom du payeur"
                        required
                        sx={{ mb: 2 }}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            sx={{ flexGrow: 1, py: 1.5 }}
                        >
                            {editingPayer ? 'Enregistrer les Modifications' : 'Ajouter le Payeur'}
                        </Button>
                        {editingPayer && (
                            <Button
                                type="button"
                                variant="outlined"
                                color="inherit"
                                onClick={handleCancelEdit}
                                sx={{ flexGrow: 1, py: 1.5 }}
                            >
                                Annuler
                            </Button>
                        )}
                    </Box>
                    {message && <Typography variant="body2" align="center" sx={{ mt: 2, color: 'text.secondary' }}>{message}</Typography>}
                </Box>

                <Typography variant="h6" component="h3" sx={{ mb: 2, color: 'text.secondary' }}>Liste des Payeurs</Typography>
                {allPayers.length === 0 ? (
                    <Typography align="center" color="text.secondary">Aucun payeur enregistré pour le moment.</Typography>
                ) : (
                    <TableContainer component={Paper} elevation={1} sx={{ borderRadius: '8px' }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Nom du Payeur</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {allPayers.map((payer) => (
                                    <TableRow key={payer.id}>
                                        <TableCell><Typography variant="body2" sx={{ fontWeight: 'medium' }}>{payer.name}</Typography></TableCell>
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                <Button
                                                    variant="contained"
                                                    color="warning"
                                                    size="small"
                                                    onClick={() => handleEditPayer(payer)}
                                                >
                                                    Modifier
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    color="error"
                                                    size="small"
                                                    onClick={() => handleDeletePayer(payer.id)}
                                                >
                                                    Supprimer
                                                </Button>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        );
    };

    // Component for managing themes
    const ManageThemes = ({ setCurrentView, fetchAllThemes, allThemes, refreshGlobalData }) => {
        const [themeName, setThemeName] = useState('');
        const [message, setMessage] = useState('');
        const [editingTheme, setEditingTheme] = useState(null);

        const handleSaveTheme = async (e) => {
            e.preventDefault();
            if (!themeName) {
                setMessage("Veuillez entrer un nom de thème.");
                return;
            }

            try {
                let response;
                if (editingTheme) {
                    response = await fetch(`${API_BASE_URL}/themes.php`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ id: editingTheme.id, name: themeName })
                    });
                } else {
                    response = await fetch(`${API_BASE_URL}/themes.php`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ name: themeName })
                    });
                }

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const result = await response.json();
                setMessage(result.message);
                setThemeName('');
                setEditingTheme(null);
                refreshGlobalData(); // Refresh the list in App.js
            } catch (error) {
                console.error("Erreur de sauvegarde du thème:", error);
                setMessage("Échec de la sauvegarde du thème.");
            }
        };

        const handleEditTheme = (theme) => {
            setEditingTheme(theme);
            setThemeName(theme.name);
            setMessage('');
        };

        const handleCancelEdit = () => {
            setEditingTheme(null);
            setThemeName('');
            setMessage('');
        };

        const handleDeleteTheme = async (id) => {
            if (window.confirm("Êtes-vous sûr de vouloir supprimer ce thème ?")) {
                try {
                    const response = await fetch(`${API_BASE_URL}/themes.php`, {
                        method: 'DELETE',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ id })
                    });
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    const result = await response.json();
                    setMessage(result.message);
                    refreshGlobalData(); // Refresh the list in App.js
                } catch (error) {
                    console.error("Erreur de suppression du thème:", error);
                    setMessage("Échec de la suppression du thème.");
                }
            }
        };

        return (
            <Paper elevation={3} sx={{ p: 4, borderRadius: '12px', maxWidth: '960px', mx: 'auto', my: 4 }}>
                <Typography variant="h4" component="h2" align="center" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary', mb: 3 }}>
                    Gérer les Thèmes
                </Typography>

                <Button
                    variant="outlined"
                    sx={{ mb: 3 }}
                    onClick={() => setCurrentView('people')}
                >
                    Retour à la Liste des Personnes
                </Button>

                <Box component="form" onSubmit={handleSaveTheme} sx={{ mb: 4, p: 3, border: '1px solid #e0e0e0', borderRadius: '8px', bgcolor: 'grey.50', boxShadow: 1 }}>
                    <Typography variant="h6" component="h3" sx={{ mb: 2, color: 'text.secondary' }}>
                        {editingTheme ? 'Modifier le Thème' : 'Ajouter un Nouveau Thème'}
                    </Typography>
                    <TextField
                        fullWidth
                        label="Nom du Thème"
                        variant="outlined"
                        value={themeName}
                        onChange={(e) => setThemeName(e.target.value)}
                        placeholder="Entrez le nom du thème"
                        required
                        sx={{ mb: 2 }}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            sx={{ flexGrow: 1, py: 1.5 }}
                        >
                            {editingTheme ? 'Enregistrer les Modifications' : 'Ajouter le Thème'}
                        </Button>
                        {editingTheme && (
                            <Button
                                type="button"
                                variant="outlined"
                                color="inherit"
                                onClick={handleCancelEdit}
                                sx={{ flexGrow: 1, py: 1.5 }}
                            >
                                Annuler
                            </Button>
                        )}
                    </Box>
                    {message && <Typography variant="body2" align="center" sx={{ mt: 2, color: 'text.secondary' }}>{message}</Typography>}
                </Box>

                <Typography variant="h6" component="h3" sx={{ mb: 2, color: 'text.secondary' }}>Liste des Thèmes</Typography>
                {allThemes.length === 0 ? (
                    <Typography align="center" color="text.secondary">Aucun thème enregistré pour le moment.</Typography>
                ) : (
                    <TableContainer component={Paper} elevation={1} sx={{ borderRadius: '8px' }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Nom du Thème</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {allThemes.map((theme) => (
                                    <TableRow key={theme.id}>
                                        <TableCell><Typography variant="body2" sx={{ fontWeight: 'medium' }}>{theme.name}</Typography></TableCell>
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                <Button
                                                    variant="contained"
                                                    color="warning"
                                                    size="small"
                                                    onClick={() => handleEditTheme(theme)}
                                                >
                                                    Modifier
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    color="error"
                                                    size="small"
                                                    onClick={() => handleDeleteTheme(theme.id)}
                                                >
                                                    Supprimer
                                                </Button>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        );
    };

    // New Component: AttendanceCalendarView
    const AttendanceCalendarView = ({ setCurrentView, allPeople, allAttendanceRecords, allThemes }) => {
        const [selectedDate, setSelectedDate] = useState(null); // State for selected date filter
        const [filterPersonId, setFilterPersonId] = useState(''); // State for person filter

        const sortedAttendance = [...allAttendanceRecords].sort((a, b) => {
            const dateA = new Date(a.startDate);
            const dateB = new Date(b.startDate);
            return dateB.getTime() - dateA.getTime(); // Sort by most recent first
        });

        const filteredAttendance = sortedAttendance.filter(record => {
            const recordStartDate = new Date(record.startDate);
            const recordEndDate = new Date(record.endDate);
            const selectedDateTime = selectedDate ? new Date(selectedDate).getTime() : null;

            const matchesDate = selectedDate ?
                (selectedDateTime >= recordStartDate.setHours(0,0,0,0) && selectedDateTime <= recordEndDate.setHours(23,59,59,999)) :
                true;

            const matchesPerson = filterPersonId ?
                parseInt(record.personId) === parseInt(filterPersonId) :
                true;

            return matchesDate && matchesPerson;
        });

        const getPersonName = (personId) => {
            const person = allPeople.find(p => parseInt(p.id) === parseInt(personId));
            return person ? person.firstName : `Personne ID: ${personId}`;
        };

        const getThemeName = (themeId) => {
            const theme = allThemes.find(t => t.name === themeId); // Assuming themeId is actually the name
            return theme ? theme.name : themeId || 'Non spécifié';
        };

        return (
            <Paper elevation={3} sx={{ p: 4, borderRadius: '12px', maxWidth: '960px', mx: 'auto', my: 4 }}>
                <Typography variant="h4" component="h2" align="center" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary', mb: 3 }}>
                    Calendrier des Présences
                </Typography>

                <Button
                    variant="outlined"
                    sx={{ mb: 3 }}
                    onClick={() => setCurrentView('people')}
                >
                    Retour à la Liste des Personnes
                </Button>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 4, p: 3, border: '1px solid #e0e0e0', borderRadius: '8px', bgcolor: 'grey.50', boxShadow: 1 }}>
                    <TextField
                        fullWidth
                        label="Filtrer par Date"
                        type="date"
                        value={selectedDate || ''}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <FormControl fullWidth>
                        <InputLabel id="person-filter-label">Filtrer par Personne</InputLabel>
                        <Select
                            labelId="person-filter-label"
                            id="person-filter"
                            value={filterPersonId}
                            label="Filtrer par Personne"
                            onChange={(e) => setFilterPersonId(e.target.value)}
                        >
                            <MenuItem value="">Toutes les personnes</MenuItem>
                            {allPeople.map((person) => (
                                <MenuItem key={person.id} value={person.id}>{person.firstName}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Typography variant="h6" component="h3" sx={{ mb: 2, color: 'text.secondary' }}>Liste des Présences</Typography>
                {filteredAttendance.length === 0 ? (
                    <Typography align="center" color="text.secondary">Aucune présence trouvée pour les filtres sélectionnés.</Typography>
                ) : (
                    <TableContainer component={Paper} elevation={1} sx={{ borderRadius: '8px' }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Personne</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Début</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Fin</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Jours</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Thème</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredAttendance.map((record) => (
                                    <TableRow key={record.id}>
                                        <TableCell><Typography variant="body2" sx={{ fontWeight: 'medium' }}>{getPersonName(record.personId)}</Typography></TableCell>
                                        <TableCell><Typography variant="body2">{new Date(record.startDate).toLocaleDateString()}</Typography></TableCell>
                                        <TableCell><Typography variant="body2">{new Date(record.endDate).toLocaleDateString()}</Typography></TableCell>
                                        <TableCell align="right"><Typography variant="body2">{record.actualDaysPresent}</Typography></TableCell>
                                        <TableCell align="right"><Typography variant="body2">{getThemeName(record.theme)}</Typography></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        );
    };


    // Main App render logic
    return (
        <ThemeProvider theme={theme}> {/* Wrap the entire app with ThemeProvider */}
            <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100', display: 'flex', flexDirection: 'column' }}>
                {isLoggedIn && (
                    <AppBar position="static">
                        <Toolbar>
                            {/* App Title (visible on all screens) */}
                            <Typography
                                variant="h6"
                                noWrap
                                component="div"
                                sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', mr: 2 }}
                            >
                                Gestion de Présences et Paiements
                            </Typography>

                            {/* User Info and Logout Button */}
                            <Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center', ml: 2 }}>
                                <Typography variant="body2" color="inherit" sx={{ mr: 1, display: { xs: 'none', sm: 'block' } }}>
                                    {currentUsername}
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="error"
                                    size="small"
                                    onClick={handleLogout}
                                >
                                    Déconnexion
                                </Button>
                            </Box>
                        </Toolbar>
                    </AppBar>
                )}

                {/* Main content area */}
                {/* Add padding-bottom to main content to prevent it from being hidden by the BottomNavigation */}
                <Box component="main" sx={{ flexGrow: 1, p: 3, width: '100%', mt: isLoggedIn ? 0 : 8, pb: isLoggedIn ? '56px' : 0 }}>
                    {!isLoggedIn ? (
                        <LoginPage onLogin={handleLogin} message={authMessage} />
                    ) : (
                        loadingGlobalData ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                                <CircularProgress />
                                <Typography sx={{ ml: 2 }}>Chargement des données...</Typography>
                            </Box>
                        ) : (
                            <>
                                {/* Render components based on current view */}
                                {currentView === 'people' && (
                                    <PeopleList
                                        setCurrentView={setCurrentView}
                                        setSelectedPerson={setSelectedPerson}
                                        allPeople={allPeople}
                                        allAttendanceRecords={allAttendanceRecords}
                                        allPaymentRecords={allPaymentRecords}
                                        calculatePersonSummary={calculatePersonSummary}
                                        refreshGlobalData={fetchGlobalData}
                                    />
                                )}
                                {currentView === 'attendance' && selectedPerson && (
                                    <AttendanceTracker
                                        selectedPerson={selectedPerson}
                                        setCurrentView={setCurrentView}
                                        allThemes={allThemes}
                                        refreshGlobalData={fetchGlobalData}
                                        exportToExcel={exportToExcel}
                                        exportToPdf={exportToPdf}
                                    />
                                )}
                                {currentView === 'summary' && (
                                    <SummaryView
                                        setCurrentView={setCurrentView}
                                        allPeople={allPeople}
                                        allAttendanceRecords={allAttendanceRecords}
                                        allPaymentRecords={allPaymentRecords}
                                        calculatePersonSummary={calculatePersonSummary}
                                        exportToExcel={exportToExcel}
                                        exportToPdf={exportToPdf}
                                    />
                                )}
                                {currentView === 'payments' && selectedPerson && (
                                    <PaymentAdjustment
                                        selectedPerson={selectedPerson}
                                        setCurrentView={setCurrentView}
                                        allPayers={allPayers}
                                        refreshGlobalData={fetchGlobalData}
                                        exportToExcel={exportToExcel}
                                        exportToPdf={exportToPdf}
                                    />
                                )}
                                {currentView === 'managePayers' && (
                                    <ManagePayers
                                        setCurrentView={setCurrentView}
                                        allPayers={allPayers}
                                        refreshGlobalData={fetchGlobalData}
                                    />
                                )}
                                {currentView === 'manageThemes' && (
                                    <ManageThemes
                                        setCurrentView={setCurrentView}
                                        allThemes={allThemes}
                                        refreshGlobalData={fetchGlobalData}
                                    />
                                )}
                                {currentView === 'calendar' && (
                                    <AttendanceCalendarView
                                        setCurrentView={setCurrentView}
                                        allPeople={allPeople}
                                        allAttendanceRecords={allAttendanceRecords}
                                        allThemes={allThemes}
                                    />
                                )}
                            </>
                        )
                    )}
                </Box>

                {isLoggedIn && (
                    <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1000 }} elevation={3}>
                        <BottomNavigation
                            showLabels
                            value={bottomNavValue}
                            onChange={(event, newValue) => {
                                setBottomNavValue(newValue);
                                // Map newValue (index) to view name
                                switch (newValue) {
                                    case 0: handleNavigate('people'); break;
                                    case 1: handleNavigate('summary'); break;
                                    case 2: handleNavigate('calendar'); break;
                                    case 3: handleOpenConfigMenu(event); break; // Open config menu when 'Config' is clicked
                                    default: handleNavigate('people');
                                }
                            }}
                        >
                            <BottomNavigationAction label="Personnes" icon={<PeopleIcon />} />
                            <BottomNavigationAction label="Résumé" icon={<AssessmentIcon />} />
                            <BottomNavigationAction label="Calendrier" icon={<CalendarMonthIcon />} />
                            <BottomNavigationAction label="Config" icon={<SettingsIcon />} />
                        </BottomNavigation>
                        {/* Config Menu (remains a dropdown, positioned relative to the BottomNavigation) */}
                        <Menu
                            id="menu-config-bottom-nav"
                            anchorEl={anchorElConfig}
                            anchorOrigin={{
                                vertical: 'top', // Position above the button
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'bottom', // Align bottom of menu with top of anchor
                                horizontal: 'right',
                            }}
                            open={Boolean(anchorElConfig)}
                            onClose={handleCloseConfigMenu}
                        >
                            <MenuItem onClick={() => handleNavigate('managePayers')}>
                                Gérer Payeurs
                            </MenuItem>
                            <MenuItem onClick={() => handleNavigate('manageThemes')}>
                                Gérer Thèmes
                            </MenuItem>
                            <MenuItem onClick={() => { fetchGlobalData(); handleCloseConfigMenu(); }}>
                                <RefreshIcon sx={{ mr: 1 }} /> Recharger Données
                            </MenuItem>
                        </Menu>
                    </Paper>
                )}
            </Box>
        </ThemeProvider>
    );
};

export default App;
